// src/components/resources/tools/generators/BuyerPersona/NewBuyerPersonaGenerator.tsx

'use client'

import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { 
  ChevronLeftIcon, 
  ChevronRightIcon, 
  CheckIcon, 
} from '@heroicons/react/24/outline'
import { clsx } from 'clsx'

import { buyerPersonaGenerator } from '~/lib/resources/data/generators/buyerPersonaGenerator'
import { PersonaProvider, usePersona } from './PersonaContext'
import { PersonaPreview } from './PersonaPreview'
import { PersonaOutput } from './PersonaOutput'
import { ImageBasedPdfExport } from './ImageBasedPdfExport'
import {
  type GeneratorField,
  // type GeneratorStep,
  type Condition,
  type RequirementCondition
} from '~/lib/resources/types/tools/generator'
import {
  formatText,
  CapitalizationStrategy
} from '~/lib/utils/formatters'
import { useStepScrollReset } from '~/components/resources/tools/generators/shared/useStepScrollReset'

// Form field type interfaces - updated to remove value and onChange
interface FormFieldProps {
  field: GeneratorField
  showValidationError: boolean
  isRequired: boolean
}

// Main generator component
export function BuyerPersonaGenerator(): JSX.Element {
  return (
    <PersonaProvider>
      <BuyerPersonaGeneratorContent />
    </PersonaProvider>
  )
}

// Content component that uses the persona context
function BuyerPersonaGeneratorContent(): JSX.Element {
  // State for UI control
  const [showPreview, setShowPreview] = useState(true)
  const [localValidationErrors, setLocalValidationErrors] = useState<Record<string, string>>({})
  const [showValidationErrors, setShowValidationErrors] = useState(false)

  // Toggle preview visibility
  const togglePreview = useCallback((): void => {
    setShowPreview(prev => !prev)
  }, [])

  const { 
    formData, 
    currentStep, 
    setCurrentStep,
    isGenerated,
    generatePersona,
    resetGenerator,
  } = usePersona()

  // Get steps from buyerPersonaGenerator config
  const steps = buyerPersonaGenerator.config.steps
  const currentStepConfig = steps[currentStep]
  // Ensure each step begins at the top of the viewport, especially on mobile
  // where the previous submit button may be far below the fold.
  const stepContainerRef = useStepScrollReset<HTMLDivElement>(currentStep)

  // Special cases for text formatting
  const specialCases = useMemo(() => ({
    // Handle common acronyms and industry terms
    'b2b': 'B2B',
    'b2c': 'B2C',
    'b2g': 'B2G',
    'd2c': 'D2C',
    'saas': 'SaaS',
    'roi': 'ROI',
    'roas': 'ROAS',
    'cpa': 'CPA',
    'cpc': 'CPC',
    'cpm': 'CPM',
    'ctr': 'CTR',
    'seo': 'SEO',
    'ppc': 'PPC',
    'smb': 'SMB',
    'ltv': 'LTV',
    // Special case terms that need specific capitalization
    'smart tv': 'Smart TV',
    'social media': 'Social Media',
    'artificial intelligence': 'Artificial Intelligence',
    'machine learning': 'Machine Learning',
    'virtual reality': 'Virtual Reality',
    'augmented reality': 'Augmented Reality',
    'internet of things': 'Internet of Things',
    'customer experience': 'Customer Experience',
    'user experience': 'User Experience',
    'user interface': 'User Interface'
  }), [])

  // Custom format function that uses formatText with our special cases
  const formatWithSpecialCases = useCallback((text: string, capitalizationStrategy = CapitalizationStrategy.TITLE_CASE) => {
    return formatText(text, { 
      capitalization: capitalizationStrategy,
      specialCases
    })
  }, [specialCases])

  // Helper function to evaluate a condition
  const evaluateCondition = useCallback((condition: Condition | RequirementCondition): boolean => {
    const { field: conditionField, operator, value } = condition
    const fieldValue = formData[conditionField]
    
    // Handle special case for businessType (treat B2G like B2B)
    if (conditionField === 'businessType') {
      const businessType = fieldValue as string ?? ''
      const isB2B = businessType === 'B2B' || businessType === 'B2G'
      
      if (operator === 'equals' && value === 'B2B') {
        return isB2B
      } else if (operator === 'notEquals' && value === 'B2B') {
        return !isB2B
      }
    }
    
    // Standard condition evaluation
    switch (operator) {
      case 'equals':
        return fieldValue === value
      case 'notEquals':
        return fieldValue !== value
      case 'contains':
        if (Array.isArray(fieldValue) && typeof value === 'string') {
          return fieldValue.includes(value)
        } else if (typeof fieldValue === 'string' && typeof value === 'string') {
          return fieldValue.includes(value)
        }
        return false
      case 'notContains':
        if (Array.isArray(fieldValue) && typeof value === 'string') {
          return !fieldValue.includes(value)
        } else if (typeof fieldValue === 'string' && typeof value === 'string') {
          return !fieldValue.includes(value)
        }
        return true
      case 'exists':
        return fieldValue !== undefined && fieldValue !== null && fieldValue !== ''
      case 'notExists':
        return fieldValue === undefined || fieldValue === null || fieldValue === ''
      case 'greaterThan':
        return typeof fieldValue === 'number' && typeof value === 'number' && fieldValue > value
      case 'lessThan':
        return typeof fieldValue === 'number' && typeof value === 'number' && fieldValue < value
      case 'greaterThanOrEquals':
        return typeof fieldValue === 'number' && typeof value === 'number' && fieldValue >= value
      case 'lessThanOrEquals':
        return typeof fieldValue === 'number' && typeof value === 'number' && fieldValue <= value
      case 'isEmpty':
        return fieldValue === undefined || fieldValue === null || fieldValue === '' || (Array.isArray(fieldValue) && fieldValue.length === 0)
      case 'isNotEmpty':
        return fieldValue !== undefined && fieldValue !== null && fieldValue !== '' && (!Array.isArray(fieldValue) || fieldValue.length > 0)
      default:
        return false
    }
  }, [formData])
  
  // Helper function to check if a field should be visible
  // Recurse via a hoisted inner function declaration so the callback never
  // references its own `const` before declaration (react-hooks/immutability).
  const isFieldVisible = useCallback((condition: Condition): boolean => {
    function check(c: Condition): boolean {
      // Handle AND conditions
      if (c.and && c.and.length > 0) {
        return c.and.every(check)
      }

      // Handle OR conditions
      if (c.or && c.or.length > 0) {
        return c.or.some(check)
      }

      // Handle single condition
      return evaluateCondition(c)
    }
    return check(condition)
  }, [evaluateCondition])

  // Helper function to check if a field is required
  const isFieldRequired = useCallback((field: GeneratorField): boolean => {
    // Check if field has basic required validation
    if (field.validation?.required) {
      return true
    }

    // Check if field has conditional required validation
    if (field.validation?.requiredIf) {
      return evaluateCondition(field.validation.requiredIf)
    }

    return false
  }, [evaluateCondition])

  // Validate the current step
  const validateCurrentStep = useCallback((): Record<string, string> => {
    const errors: Record<string, string> = {}
    
    if (!currentStepConfig?.fields) return errors
    
    // Get fields for current step
    const currentStepFields = currentStepConfig.fields
    
    // Check each field in the current step
    currentStepFields.forEach((field) => {
      // Skip validation if field is not visible based on conditions
      if (field.showIf && !isFieldVisible(field.showIf)) {
        return
      }
      
      // Skip validation if field has a value
      const fieldValue = formData[field.id]
      const isEmpty = 
        fieldValue === undefined || 
        fieldValue === null || 
        fieldValue === '' || 
        (Array.isArray(fieldValue) && fieldValue.length === 0)
      
      if (!isEmpty) return
      
      // Check if field is required based on conditions
      const required = isFieldRequired(field)
      
      // Add error if field is required and empty
      if (required && isEmpty) {
        errors[field.id] = field.validation?.requiredIf?.errorMessage ?? `${field.label} is required.`
      }
    })
    
    return errors
  }, [currentStepConfig, formData, isFieldVisible, isFieldRequired])

  // Go to next step with validation
  const handleNextStep = useCallback(() => {
    // Validate current step
    const validationErrors = validateCurrentStep()
    setLocalValidationErrors(validationErrors)
    setShowValidationErrors(true)
    
    // If there are errors, don't proceed
    if (Object.keys(validationErrors).length > 0) {
      return
    }
    
    // Proceed to next step or generate on last step
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1)
      setShowValidationErrors(false)
    } else {
      // Generate persona if on last step
      generatePersona()
    }
  }, [currentStep, validateCurrentStep, steps.length, generatePersona, setCurrentStep])

  // Go to previous step
  const handlePrevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
      setShowValidationErrors(false)
    }
  }, [currentStep, setCurrentStep])

  // Render field with updated component that uses context directly
  const renderField = useCallback((field: GeneratorField, hasError: boolean): JSX.Element | null => {
    const fieldType = field.type
    const isRequired = isFieldRequired(field)
    
    // Handle different field types
    if (fieldType === 'text' || field.type === 'email' || field.type === 'url' || field.type === 'date') {
      return (
        <TextField
          field={field}
          showValidationError={hasError}
          isRequired={isRequired}
        />
      )
    }
      
    if (fieldType === 'select') {
      return (
        <SelectField
          field={field}
          showValidationError={hasError}
          isRequired={isRequired}
        />
      )
    }
      
    if (fieldType === 'multiselect') {
      return (
        <MultiSelectField
          field={field}
          showValidationError={hasError}
          isRequired={isRequired}
        />
      )
    }
      
    if (fieldType === 'textarea' || field.type === 'markdown') {
      return (
        <TextAreaField
          field={field}
          showValidationError={hasError}
          isRequired={isRequired}
        />
      )
    }
      
    // Default case
    return null
  }, [isFieldRequired])

  // Render form fields for current step
  const renderFormFields = useCallback(() => {
    if (!currentStepConfig?.fields) return null
    
    return currentStepConfig.fields.map((field) => {
      // Check visibility conditions
      if (field.showIf && !isFieldVisible(field.showIf)) {
        return null
      }
      
      const hasError = Boolean(showValidationErrors && localValidationErrors[field.id])
      const isRequired = isFieldRequired(field)
      
      return (
        <div key={field.id} className="mb-6">
          <label htmlFor={field.id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {field.label}
            {isRequired && 
              <span className="ml-1 text-red-500">*</span>
            }
          </label>
          
          {field.description && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{field.description}</p>
          )}
          
          {renderField(field, hasError)}
          
          {hasError && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{localValidationErrors[field.id]}</p>
          )}
        </div>
      )
    })
  }, [currentStepConfig, isFieldVisible, showValidationErrors, localValidationErrors, renderField, isFieldRequired])

  // Main return rendering both form and result
  return (
    <div className="bg-gray-50 dark:bg-gray-900 p-3 sm:p-5 lg:p-7 py-4 sm:py-6 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700">
      {/* Streamlined header with navigation and controls */}
      <div className="mb-6">
        {isGenerated ? (
          <div className="flex justify-end mb-6">
            <button
              onClick={resetGenerator}
              className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-900"
            >
              Create New Persona
            </button>
          </div>
        ) : (
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">Create Your Buyer Persona</h2>
            <button
              type="button"
              onClick={togglePreview}
              className="inline-flex items-center px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-xs sm:text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {showPreview ? (
                <>
                  <ChevronRightIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                  <span className="whitespace-nowrap">Hide Preview</span>
                </>
              ) : (
                <>
                  <ChevronLeftIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                  <span className="whitespace-nowrap">Show Preview</span>
                </>
              )}
            </button>
          </div>
        )}
        
        {/* Step progress indicators - only show when not generated */}
        {!isGenerated && (
          <div className="relative mb-4 overflow-hidden">
            <div className="flex justify-between items-center relative z-10 pb-2">
              {steps.map((step, index) => {
                const isCompleted = index < currentStep
                const isCurrent = index === currentStep
                // Allow clicking on completed steps or the next step
                const isClickable = isCompleted || index === currentStep + 1
                
                return (
                  <div 
                    key={index}
                    className={`
                      flex flex-col items-center relative z-10
                      ${isClickable ? 'cursor-pointer' : ''}
                    `}
                    onClick={() => isClickable && setCurrentStep(index)}
                  >
                    <div className={`
                      w-8 h-8 rounded-full flex items-center justify-center border-2
                      ${isCompleted ? 'bg-blue-600 border-blue-600' : ''}
                      ${isCurrent ? 'bg-white dark:bg-gray-800 border-blue-600' : ''}
                      ${!isCompleted && !isCurrent ? 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600' : ''}
                    `}>
                      {isCompleted ? (
                        <CheckIcon className="w-4 h-4 text-white" />
                      ) : (
                        <span className={`
                          text-xs font-medium
                          ${isCurrent ? 'text-blue-600' : 'text-gray-500 dark:text-gray-400'}
                        `}>
                          {index + 1}
                        </span>
                      )}
                    </div>
                    <div className={`
                      mt-2 text-xs font-medium text-center
                      ${isCurrent ? 'text-blue-600' : ''}
                      ${isCompleted ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}
                    `}>
                      <span className="inline-block w-16 sm:w-20 md:w-24 overflow-hidden text-ellipsis" style={{ lineHeight: '1.2em', height: '2.4em' }}>
                        {step.title}
                      </span>
                    </div>
                  </div>
                )
              })}
              
              {/* Progress bar */}
              <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-200 dark:bg-gray-700">
                <div 
                  className="h-full bg-blue-600 transition-all duration-300" 
                  style={{ width: `${Math.max(0, currentStep) / Math.max(1, steps.length - 1) * 100}%` }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Main content - form and preview/output */}
      <div className={clsx(
        "flex flex-col lg:flex-row gap-8",
        isGenerated && "flex-col"
      )}>
        {/* Left column - form */}
        {!isGenerated && (
          <div
            ref={stepContainerRef}
            className={clsx(
            "flex-1 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden",
            showPreview ? "lg:w-1/2" : "lg:w-full"
          )}
          >
            {/* Progress */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between mb-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Step {currentStep + 1} of {steps.length}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {Math.round(((currentStep + 1) / steps.length) * 100)}% Complete
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                <div 
                  className="bg-blue-600 h-2.5 rounded-full" 
                  style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
                ></div>
              </div>
            </div>
            
            {/* Step title */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {formatWithSpecialCases(currentStepConfig?.title ?? 'Step Information')}
              </h2>
              {currentStepConfig?.description && (
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{currentStepConfig.description}</p>
              )}
            </div>
            
            {/* Form fields */}
            <div className="px-6 py-6 bg-white dark:bg-gray-800">
              {renderFormFields()}
            </div>
            
            {/* Navigation buttons */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 flex justify-between">
              <button
                onClick={handlePrevStep}
                disabled={currentStep === 0}
                className={clsx(
                  "inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md",
                  currentStep === 0
                    ? "text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 cursor-not-allowed"
                    : "text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-800"
                )}
              >
                <ChevronLeftIcon className="h-4 w-4 mr-2" />
                Previous
              </button>
              
              <button
                onClick={handleNextStep}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-800"
              >
                {currentStep === steps.length - 1 ? 'Generate Persona' : 'Next'}
                <ChevronRightIcon className="h-4 w-4 ml-2" />
              </button>
            </div>
          </div>
        )}
        
        {/* Right column - preview or output */}
        <div className={clsx(
          "flex-1 flex flex-col",
          isGenerated ? "w-full" : (showPreview ? "lg:w-1/2" : "hidden"),
        )}>
          {isGenerated ? (
            <div className="persona-output-container bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Your Buyer Persona</h2>
                <div className="flex space-x-2">
                  <ImageBasedPdfExport />
                </div>
              </div>
              
              <PersonaOutput />
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-2 sm:p-4 md:p-6 py-4 sm:py-6 ">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Preview</h2>
                <span className="text-xs text-gray-500 dark:text-gray-400">Updates as you type</span>
              </div>
              
              <div className="w-full max-w-full overflow-x-hidden">
                <PersonaPreview stepId={currentStepConfig?.id ?? ''} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Updated Form field components that use context directly
function TextField({ field, showValidationError }: FormFieldProps): JSX.Element {
  const { formData, updateField } = usePersona()
  const value = (formData[field.id] as string) ?? ''
  
  return (
    <input
      type={field.type === 'email' || field.type === 'url' || field.type === 'date' ? field.type : 'text'}
      id={field.id}
      name={field.id}
      value={value}
      onChange={(e) => updateField(field.id, e.target.value)}
      placeholder={field.placeholder}
      className={clsx(
        "mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm text-gray-900 dark:text-gray-200 dark:bg-gray-700",
        showValidationError && "border-red-300 dark:border-red-500"
      )}
    />
  )
}

function SelectField({ field, showValidationError }: FormFieldProps): JSX.Element {
  const { formData, updateField } = usePersona()
  const value = (formData[field.id] as string) || ''
  
  return (
    <select
      id={field.id}
      name={field.id}
      value={value}
      onChange={(e) => updateField(field.id, e.target.value)}
      className={clsx(
        "mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm text-gray-900 dark:text-gray-200 dark:bg-gray-700",
        showValidationError && "border-red-300 dark:border-red-500"
      )}
    >
      <option value="">Select an option</option>
      {field.options?.map((option) => (
        <option key={String(option.value)} value={String(option.value)}>
          {option.label}
        </option>
      ))}
    </select>
  )
}

function MultiSelectField({ field, showValidationError }: FormFieldProps): JSX.Element {
  const { formData, updateField } = usePersona()
  const value = (formData[field.id] as string[]) || []
  const [isOpen, setIsOpen] = useState(false)
  
  const toggleOption = (optionValue: string): void => {
    const isSelected = value?.includes(optionValue)
    
    if (isSelected) {
      updateField(field.id, value.filter(val => val !== optionValue))
    } else {
      updateField(field.id, [...(value || []), optionValue])
    }
  }
  
  // Close dropdown when clicking outside
  const dropdownRef = useRef<HTMLDivElement>(null)
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])
  
  return (
    <div className="mt-1 relative" ref={dropdownRef}>
      {/* Selected options display with badges */}
      <div 
        className={clsx(
          "flex flex-wrap gap-2 p-2 border rounded-md min-h-[42px] cursor-pointer",
          isOpen ? "border-blue-500 ring-1 ring-blue-500" : "border-gray-300 dark:border-gray-600",
          showValidationError && "border-red-300 dark:border-red-500",
          "bg-white dark:bg-gray-700"
        )}
        onClick={() => setIsOpen(!isOpen)}
      >
        {value.length > 0 ? (
          value.map((selectedValue) => {
            // Find the matching option
            const option = field.options?.find((opt) => opt.value === selectedValue)
            return (
              <div key={selectedValue} className="inline-flex items-center bg-blue-50 dark:bg-blue-900 rounded-md px-2 py-1 text-sm">
                <span className="text-blue-700 dark:text-blue-200">{option?.label ?? selectedValue}</span>
                <button
                  type="button"
                  className="ml-1 text-blue-500 hover:text-blue-700 dark:text-blue-300 dark:hover:text-blue-100"
                  onClick={(e) => {
                    e.stopPropagation() // Prevent toggling dropdown
                    toggleOption(selectedValue)
                  }}
                >
                  &times;
                </button>
              </div>
            )
          })
        ) : (
          <span className="text-gray-400 dark:text-gray-500 text-sm">Select options</span>
        )}
      </div>
      
      {/* Dropdown options */}
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {field.options?.map((option) => {
            const optionValue = String(option.value)
            const isSelected = value?.includes(optionValue)
            
            return (
              <div 
                key={option.value} 
                className={clsx(
                  "flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600",
                  isSelected && "bg-blue-50 dark:bg-blue-900"
                )}
                onClick={() => toggleOption(optionValue)}
              >
                <div className={clsx(
                  "w-4 h-4 mr-2 flex-shrink-0 rounded border",
                  isSelected 
                    ? "bg-blue-600 border-blue-600" 
                    : "border-gray-300 dark:border-gray-500"
                )}>
                  {isSelected && (
                    <CheckIcon className="w-4 h-4 text-white" />
                  )}
                </div>
                <span className={isSelected ? "font-medium text-blue-700 dark:text-blue-200" : "text-gray-900 dark:text-gray-100"}>
                  {option.label}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function TextAreaField({ field, showValidationError }: FormFieldProps): JSX.Element {
  const { formData, updateField } = usePersona()
  const value = (formData[field.id] as string) || ''
  
  return (
    <textarea
      id={field.id}
      name={field.id}
      value={value}
      onChange={(e) => updateField(field.id, e.target.value)}
      placeholder={field.placeholder}
      rows={5}
      className={clsx(
        "mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm text-gray-900 dark:text-gray-200 dark:bg-gray-700",
        showValidationError && "border-red-300 dark:border-red-500"
      )}
    />
  )
} 

export function TabbedMultiSelectField({ field, showValidationError }: FormFieldProps): JSX.Element {
  const { formData, updateField } = usePersona()
  const value = (formData[field.id] as string[]) || []
  
  // Ensure value is always an array of strings
  const selectedValues = Array.isArray(value) ? value : []

  return (
    <div className="mt-1">
      <div className={clsx(
        "flex flex-wrap gap-2 p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 min-h-[42px] mb-1",
        showValidationError && "border-red-500 dark:border-red-500"
      )}>
        {selectedValues.length > 0 ? (
          selectedValues.map((selectedValue): JSX.Element => {
            // Find the matching option
            const option = field.options?.find((opt) => String(opt.value) === selectedValue)
            return (
              <div key={selectedValue} className="inline-flex items-center bg-blue-50 dark:bg-blue-900 rounded-md px-2 py-1 text-sm">
                <span className="text-blue-800 dark:text-blue-200">{option?.label ?? selectedValue}</span>
                <button
                  type="button"
                  onClick={() => updateField(field.id, selectedValues.filter(v => v !== selectedValue))}
                  className="ml-1 text-blue-500 dark:text-blue-300 hover:text-blue-700 dark:hover:text-blue-100 focus:outline-none"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            )
          })
        ) : (
          <div className="text-sm text-gray-500 dark:text-gray-400">Select options below</div>
        )}
      </div>
      
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
        {field.options?.map((option) => {
          const isSelected = selectedValues.includes(String(option.value))
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => {
                if (isSelected) {
                  updateField(field.id, selectedValues.filter(v => v !== String(option.value)))
                } else {
                  updateField(field.id, [...selectedValues, String(option.value)])
                }
              }}
              className={clsx(
                "px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500",
                isSelected
                  ? "bg-blue-100 dark:bg-blue-900 border-blue-300 dark:border-blue-700 text-blue-800 dark:text-blue-200"
                  : "bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600"
              )}
            >
              {option.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}
